import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader, useToast } from '@lms/ui';
import { PERMISSIONS } from '@lms/shared-types';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { SearchInput } from '@/components/SearchInput';
import { usePermissions } from '@/hooks/usePermissions';
import { api } from '@/lib/api';
import { permissionMatrixEditSuccessToast, roleActionErrorToast } from '@/lib/user-feedback';
import { normalizeSearch } from '@/lib/search-normalize';
import {
  PERMISSION_CATEGORIES,
  hierarchyLevelLabel,
  isSystemRole,
  permissionLabel,
  roleHierarchyLevel,
  roleLabel,
  sortRolesByHierarchy,
  type PermissionCategory,
} from '@/lib/role-display';

interface RoleRow {
  id: string;
  name: string;
  hierarchyLevel?: number;
  permissions: string[];
}

function sortRoles(roles: RoleRow[]): RoleRow[] {
  return sortRolesByHierarchy(roles);
}

function permissionMatchesSearch(category: PermissionCategory, permission: string, term: string): boolean {
  const needle = normalizeSearch(term);
  if (!needle) return true;
  const sectionBlob = normalizeSearch(`${category.label} ${category.permissions.map(permissionLabel).join(' ')}`);
  const rowSearch = normalizeSearch(permissionLabel(permission));
  return sectionBlob.includes(needle) || rowSearch.includes(needle);
}

export function PermissionsPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { canManageRole, hasPermission } = usePermissions();
  const canEditRoles = hasPermission(PERMISSIONS.ROLES_EDITAR);
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [allExpanded, setAllExpanded] = useState(true);
  const [filterOpen, setFilterOpen] = useState(false);
  const [hiddenCategories, setHiddenCategories] = useState<Set<string>>(new Set());
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const filterRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['permissions', 'matrix'],
    queryFn: () => api<{ roles: RoleRow[] }>('/permissions/matrix'),
  });

  const roles = useMemo(() => sortRoles(data?.roles ?? []), [data?.roles]);
  const selectedRoleName = searchParams.get('role') ?? roles[0]?.name ?? '';
  const selectedRole = roles.find((r) => r.name === selectedRoleName) ?? roles[0];
  const selectedRoleLabel = selectedRole ? roleLabel(selectedRole.name) : '';
  const selectedIsSystem = selectedRole ? isSystemRole(selectedRole.name) : false;
  const selectedLevel = selectedRole
    ? selectedRole.hierarchyLevel ?? roleHierarchyLevel(selectedRole.name)
    : null;
  const canEditSelectedRole = selectedRole
    ? canEditRoles && canManageRole(selectedRole.name, selectedRole.hierarchyLevel)
    : false;

  useEffect(() => {
    if (selectedRole) {
      setChecked(new Set(selectedRole.permissions));
    }
  }, [selectedRole?.id, selectedRole?.permissions.join('|')]);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) {
        setFilterOpen(false);
      }
    }
    if (filterOpen) document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [filterOpen]);

  const activeCount = checked.size;
  const searchTerm = search.trim();

  const visibleCategories = useMemo(
    () =>
      PERMISSION_CATEGORIES.filter((category) => {
        if (hiddenCategories.has(category.key)) return false;
        if (!searchTerm) return true;
        return normalizeSearch(`${category.label} ${category.permissions.map(permissionLabel).join(' ')}`).includes(
          normalizeSearch(searchTerm),
        );
      }),
    [hiddenCategories, searchTerm],
  );

  const saveMutation = useMutation({
    mutationFn: () =>
      api(`/roles/${selectedRole!.id}`, {
        method: 'PATCH',
        body: { permissions: [...checked] },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['permissions', 'matrix'] });
      toast(permissionMatrixEditSuccessToast());
    },
    onError: (error) => {
      toast(roleActionErrorToast(error, 'No se pudieron guardar los permisos'));
    },
  });

  function togglePermission(permission: string, isOn: boolean) {
    if (!canEditSelectedRole) return;
    setChecked((prev) => {
      const next = new Set(prev);
      if (isOn) next.add(permission);
      else next.delete(permission);
      return next;
    });
  }

  function toggleCategoryPermissions(permissions: string[]) {
    if (!canEditSelectedRole) return;
    setChecked((prev) => {
      const next = new Set(prev);
      const allOn = permissions.every((p) => next.has(p));
      for (const p of permissions) {
        if (allOn) next.delete(p);
        else next.add(p);
      }
      return next;
    });
  }

  function toggleCategoryFilter(key: string) {
    setHiddenCategories((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function showAllCategories() {
    setHiddenCategories(new Set());
  }

  function handleCancel() {
    if (selectedRole) setChecked(new Set(selectedRole.permissions));
  }

  return (
    <div className="lms-permission-matrix mb-24 font-sans">
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-start gap-4">
          <HubBackLink to="/administracion?tab=users" label="Volver a Administración" />
          <div>
            <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">Matriz de permisos</h1>
            <p className="mt-1 max-w-2xl text-dash-on-surface-variant">
              Configure los permisos asignados a cada rol del sistema
            </p>
          </div>
        </div>

        {selectedRole && (
          <div className="flex w-full flex-col rounded-lg bg-[#2e3a8c] px-6 py-3 text-[#9ea9ff] md:w-auto md:items-end">
            <span className="pm-label-sm uppercase tracking-wider opacity-80">Editando Permisos de:</span>
            <div className="mt-1 flex items-center gap-2">
              <span className="material-symbols-outlined text-2xl" aria-hidden="true">
                admin_panel_settings
              </span>
              <span className="pm-headline-md text-[#9ea9ff]">
                {selectedRoleLabel}
                {selectedIsSystem ? ' (Sistema)' : ''}
              </span>
            </div>
            <span className="pm-label-sm mt-1" id="pm-total-active-label">
              {activeCount} {activeCount === 1 ? 'permiso activo' : 'permisos activos'} en total
              {selectedLevel != null ? ` · ${hierarchyLevelLabel(selectedLevel)}` : ''}
            </span>
          </div>
        )}
      </div>

      {error && (
        <p className="mb-4 text-sm text-error-600" role="alert">
          {(error as Error).message}
        </p>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader />
        </div>
      ) : roles.length === 0 ? (
        <div className="pm-border pm-radius-xl border bg-white p-12 text-center pm-text-variant">
          No hay roles configurados en el sistema.
        </div>
      ) : !selectedRole ? (
        <div className="pm-border pm-radius-xl border bg-white p-12 text-center pm-text-variant">
          Seleccione un rol para editar permisos.
        </div>
      ) : (
        <>
          <div className="pm-border mb-8 flex overflow-x-auto border-b">
            {roles.map((role) => {
              const isActive = role.id === selectedRole.id;
              return (
                <Link
                  key={role.id}
                  to={`/permisos?role=${encodeURIComponent(role.name)}`}
                  onClick={() => setSearchParams({ role: role.name })}
                  className={`pm-label-md shrink-0 whitespace-nowrap border-b-2 px-6 py-3 transition-colors ${
                    isActive ? 'border-[#142175] text-[#142175]' : 'border-transparent pm-text-variant hover:text-[#142175]'
                  }`}
                >
                  {roleLabel(role.name)} ({hierarchyLevelLabel(role.hierarchyLevel ?? roleHierarchyLevel(role.name))})
                </Link>
              );
            })}
          </div>

          {!canEditSelectedRole && (
            <p className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" role="alert">
              Solo puede ver este rol. Su nivel jerárquico no le permite modificar sus permisos.
            </p>
          )}

          <div className="mb-10 flex flex-wrap gap-4">
              <div className="max-w-md min-w-[16rem] flex-1 grow">
                <SearchInput
                  value={search}
                  onChange={setSearch}
                  placeholder="Buscar categoría o permiso..."
                  aria-label="Buscar categoría o permiso"
                />
              </div>

              <div className="relative" ref={filterRef}>
                <button
                  type="button"
                  className="pm-label-md pm-border pm-radius-lg inline-flex items-center gap-2 border bg-white px-4 py-2 transition-colors hover:bg-[#f3f3f6]"
                  onClick={() => setFilterOpen((v) => !v)}
                  aria-expanded={filterOpen}
                >
                  <span className="material-symbols-outlined text-sm" aria-hidden="true">
                    filter_list
                  </span>
                  Filtrar Categorías
                </button>
                {filterOpen && (
                  <div className="pm-border absolute right-0 z-20 mt-2 w-64 rounded-xl border bg-white py-2 shadow-lg">
                    <button
                      type="button"
                      className="pm-label-sm block w-full px-4 py-2 text-left text-[#142175] hover:bg-[#f3f3f6]"
                      onClick={showAllCategories}
                    >
                      Mostrar todas
                    </button>
                    <div className="my-1 border-t border-[#c6c5d3]" />
                    {PERMISSION_CATEGORIES.map((category) => (
                      <label
                        key={category.key}
                        className="pm-label-md flex cursor-pointer items-center gap-3 px-4 py-2 hover:bg-[#f3f3f6]"
                      >
                        <input
                          type="checkbox"
                          className="pm-checkbox"
                          checked={!hiddenCategories.has(category.key)}
                          onChange={() => toggleCategoryFilter(category.key)}
                        />
                        <span>{category.label}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <button
                type="button"
                className="pm-label-md pm-border pm-radius-lg inline-flex items-center gap-2 border bg-white px-4 py-2 transition-colors hover:bg-[#f3f3f6]"
                onClick={() => setAllExpanded((v) => !v)}
              >
                {allExpanded ? 'Contraer todo' : 'Expandir todo'}
              </button>
            </div>

          <form
            id="permission-matrix-form"
            onSubmit={(e) => {
              e.preventDefault();
              saveMutation.mutate();
            }}
          >
            <div className="flex flex-col gap-12">
              {visibleCategories.map((category) => {
                const activeInGroup = category.permissions.filter((p) => checked.has(p)).length;
                const visiblePermissions = category.permissions.filter((p) =>
                  permissionMatchesSearch(category, p, searchTerm),
                );

                if (visiblePermissions.length === 0 && searchTerm) return null;

                return (
                  <section key={category.key} className="category-section">
                    <div className="pm-border mb-4 flex flex-wrap items-center justify-between gap-3 border-b pb-4">
                      <div className="flex items-center gap-3">
                        <span className="material-symbols-outlined text-[28px] text-[#142175]" aria-hidden="true">
                          {category.icon}
                        </span>
                        <h3 className="pm-headline-md text-[#1a1c1e]">{category.label}</h3>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="pm-active-badge pm-label-sm rounded-full px-3 py-1 font-bold uppercase tracking-wider">
                          {activeInGroup} {activeInGroup === 1 ? 'Activo' : 'Activos'}
                        </span>
                        <button
                          type="button"
                          className="pm-label-sm flex shrink-0 items-center gap-1 text-[#142175] hover:underline"
                          onClick={() => toggleCategoryPermissions(category.permissions)}
                        >
                          <span className="material-symbols-outlined text-sm" aria-hidden="true">
                            done_all
                          </span>
                          Seleccionar todo
                        </button>
                      </div>
                    </div>

                    {allExpanded && (
                      <div className="pm-border pm-radius-xl flex flex-col overflow-hidden border bg-white">
                        {category.permissions.map((permission) => {
                          if (!permissionMatchesSearch(category, permission, searchTerm)) return null;
                          const isChecked = checked.has(permission);
                          return (
                            <label
                              key={permission}
                              className="permission-row group flex cursor-pointer items-center gap-4 border-b px-6 py-4 transition-colors pm-border last:border-b-0"
                            >
                              <input
                                type="checkbox"
                                name="permissions[]"
                                value={permission}
                                className="pm-checkbox"
                                checked={isChecked}
                                disabled={!canEditSelectedRole}
                                onChange={(e) => togglePermission(permission, e.target.checked)}
                              />
                              <span className="pm-body-md text-[#1a1c1e] transition-colors group-hover:text-[#142175]">
                                {permissionLabel(permission)}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </section>
                );
              })}
            </div>
          </form>

          <div className="lms-permission-matrix-action-bar pm-border fixed bottom-0 left-0 z-40 w-full border-t bg-[#f9f9fc] py-4">
            <div className="lms-site-admin-sticky-bar__inner flex items-center justify-between gap-4">
              <div className="pm-label-md pm-text-variant hidden items-center gap-4 md:flex">
                <span className="material-symbols-outlined text-[20px]" aria-hidden="true">
                  info
                </span>
                Los cambios afectarán a todos los usuarios con el rol &quot;{selectedRoleLabel}&quot;.
              </div>
              <div className="flex w-full gap-4 md:w-auto">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="pm-label-md pm-border pm-radius-pill pm-text-variant inline-flex flex-1 items-center justify-center border bg-white px-8 py-2.5 transition-colors hover:bg-[#f3f3f6] md:flex-none"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  form="permission-matrix-form"
                  disabled={saveMutation.isPending || !canEditSelectedRole}
                  className="pm-label-md inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#142175] px-10 py-2.5 text-white shadow-lg shadow-[#142175]/20 transition-all hover:bg-[#2e3a8c] active:scale-95 disabled:opacity-60 md:flex-none"
                >
                  <span className="material-symbols-outlined text-[20px]" aria-hidden="true">
                    save
                  </span>
                  {saveMutation.isPending ? 'Guardando…' : 'Guardar cambios'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
